import { useMemo } from "react";
import { useQuery } from "@apollo/client";

import { AccountContext } from "views/AccountWrapper";
import useTitle from "hooks/useTitle";
import { ApiKey } from "types/generated";
import useTypedContext from "hooks/useTypedContext";
import useAnalytics from "hooks/useAnalytics";
import ApiKeysEmptyList from "components/ApiKeys/EmptyList";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import PageLoading from "components/loading/PageLoading";
import FlashContext from "components/FlashMessages/FlashContext";
import useErrorHandle from "hooks/useErrorHandle";
import NotFoundPage from "components/error/NotFoundPage";

import ApiKeysListV2 from "./List";
import ApiKeysPageLayout from "./PageLayout";
import { getManagementStrategy } from "../helpers";
import { SettingsContext } from "../Context";
import { showApiKeysCreateDrawer } from "./CreateDrawer";
import { GET_API_KEYS } from "./gql";
import { showApiKeysInfoDrawer } from "./InfoDrawer";

const byMostRecentUsage = (x: ApiKey, y: ApiKey) => (y.lastUsedAt || 0) - (x.lastUsedAt || 0);

const REFETCH_QUERIES = [GET_API_KEYS];

const ApiKeysV2 = () => {
  const { activationStatus } = useTypedContext(SettingsContext);
  const { accountName } = useTypedContext(AccountContext);
  const { onError } = useTypedContext(FlashContext);

  const { loading, data, error } = useQuery(GET_API_KEYS, {
    onError,
  });

  useTitle(`Organization Settings · API Keys · ${accountName}`);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationApiKeys,
    defaultCallbackTrackProperties: { managementStrategy: getManagementStrategy(activationStatus) },
  });

  const memoizedApiKeys = useMemo<ApiKey[]>(() => {
    return data?.apiKeys ? [...data.apiKeys].sort(byMostRecentUsage) : [];
  }, [data?.apiKeys]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data) {
    return <PageLoading />;
  }

  if (!data) {
    return <NotFoundPage />;
  }

  const openInfoDrawer = () => {
    trackSegmentAnalyticsEvent("Info Click");
    showApiKeysInfoDrawer();
  };

  const openCreateApiKeyDrawer = () => {
    trackSegmentAnalyticsEvent("Create Key");
    showApiKeysCreateDrawer({
      activationStatus,
      refetchQueries: REFETCH_QUERIES,
      analyticsPage: AnalyticsPageOrganization.OrganizationApiKeys,
    });
  };

  return (
    <ApiKeysPageLayout
      openCreateApiKeyDrawer={openCreateApiKeyDrawer}
      openInfoDrawer={openInfoDrawer}
      hasApiKeys={memoizedApiKeys.length > 0}
    >
      {memoizedApiKeys.length > 0 && (
        <ApiKeysListV2
          apiKeys={memoizedApiKeys}
          refetchQueriesOnDelete={REFETCH_QUERIES}
          refetchQueriesOnUpdate={REFETCH_QUERIES}
        />
      )}
      {memoizedApiKeys.length === 0 && <ApiKeysEmptyList />}
    </ApiKeysPageLayout>
  );
};

export default ApiKeysV2;
